import { useState, type ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
    Badge,
    Box,
    Button,
    Drawer,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useListContext } from 'react-admin';

import { SidebarLiveSearch } from '@/components/filters/SidebarLiveSearch';

/**
 * Desktop: fixed aside with search + filter lists.
 * Mobile (< md): full-width search row; filter lists open in a bottom drawer.
 */
export const ListFilterSidebar = ({
    searchPlaceholder,
    children,
}: {
    searchPlaceholder: string;
    children?: ReactNode;
}) => {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(false);
    const { filterValues } = useListContext();
    const hasFilterControls = Boolean(children);
    const hasActiveFilters = Object.keys(filterValues || {}).some(
        key => key !== 'q',
    );

    if (!isSmall) {
        return (
            <Box
                sx={{
                    width: '13em',
                    minWidth: '13em',
                    order: -1,
                    mr: 2,
                    mt: 5,
                    flexShrink: 0,
                }}
            >
                <SidebarLiveSearch placeholder={searchPlaceholder} />
                {children}
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    order: -1,
                    px: 1,
                    pt: 1,
                    pb: 0.5,
                    flexShrink: 0,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SidebarLiveSearch placeholder={searchPlaceholder} />
                    </Box>
                    {hasFilterControls && (
                        <Badge
                            color="primary"
                            variant="dot"
                            invisible={!hasActiveFilters}
                            overlap="circular"
                        >
                            <Button
                                variant="outlined"
                                size="medium"
                                startIcon={<FilterListIcon />}
                                onClick={() => setOpen(true)}
                                aria-label="Open filters"
                                sx={{
                                    minHeight: 48,
                                    minWidth: 48,
                                    px: 1.5,
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Filters
                            </Button>
                        </Badge>
                    )}
                </Stack>
            </Box>
            {hasFilterControls && (
                <Drawer
                    anchor="bottom"
                    open={open}
                    onClose={() => setOpen(false)}
                    PaperProps={{
                        sx: {
                            maxHeight: '75vh',
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            px: 2,
                            pb: 3,
                            overflow: 'auto',
                        },
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            py: 1.5,
                            position: 'sticky',
                            top: 0,
                            bgcolor: 'background.paper',
                            zIndex: 1,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            Filters
                        </Typography>
                        <IconButton
                            aria-label="Close filters"
                            onClick={() => setOpen(false)}
                            sx={{ minWidth: 44, minHeight: 44 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                    <Box
                        sx={{
                            // Comfortable tap targets for FilterListItem rows.
                            '& .MuiListItemButton-root': { minHeight: 48 },
                        }}
                    >
                        {children}
                    </Box>
                </Drawer>
            )}
        </>
    );
};
